import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router";

/**
 * Runs `onTrigger` when a key appears in the URL hash (`#key` or `#key=value`), then strips it
 * from the hash. Lets a link ask the app to do something on whichever page the user is already
 * on, leaving the path, query, and navigation state untouched, and fires again every time the link is followed.
 */
const useUrlHashTrigger = (key: string, onTrigger: (value: string) => void) => {
  const { pathname, search, hash, state } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const hashParams = new URLSearchParams(hash.slice(1));
    const value = hashParams.get(key);

    if (value === null) {
      return;
    }

    hashParams.delete(key);
    const remainingHash = hashParams.toString();

    navigate(
      { pathname, search, hash: remainingHash ? `#${remainingHash}` : "" },
      { replace: true, state }
    );

    onTrigger(value);
  }, [hash, key, navigate, onTrigger, pathname, search, state]);
};

export default useUrlHashTrigger;
